import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import * as Yup from "yup";

import {
  ClassItem,
  Session,
  System,
  getAllSessions,
  getAllSystems,
  getClassesBySystem,
} from "../api/lookup";
import { submitRegistration } from "../api/registration";
import {
  gender,
  karachiAreas,
  referrers,
} from "../data/options";
import { campuses } from "../data/campuses";

interface RegistrationFormProps {
  onSubmitted: (registrationNumber: string) => void;
  // Mirrors the selected campus up to App.tsx, purely for display (footer /
  // contact modal address) — the form itself is the source of truth via
  // formik.values.branch.
  onCampusChange: (campusId: string) => void;
}

const initialValues = {
  session: "",
  system: "",
  appliedClass: "",
  firstName: "",
  lastName: "",
  gender: "",
  dateOfBirth: "",
  category: "",

  fatherName: "",
  fatherPhoneNumber: "",
  fatherEmail: "",

  motherName: "",

  area: "",

  referrer: "",
  otherReferrer: "",

  // Was a hidden, build-time-fixed campus (REACT_APP_DEFAULT_BRANCH_ID) on
  // the old single-campus Eon site. Metropolitan Academy has 5 public
  // campuses, so this is now a real field the parent picks themselves — see
  // the Campus select below.
  branch: "",
};

const SITE_KEY = process.env.REACT_APP_SITE_KEY as string;

const phoneTransform = (raw: string) => {
  if (!raw) return "";
  const v = String(raw).trim();
  if (!v) return "";
  return v.startsWith("+92") ? v : `+92${v.replace(/\D/g, "")}`;
};

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onSubmitted,
  onCampusChange,
}) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [otherRef, setOtherRef] = useState(false);

  // Verified at submit time, not as a gate before the form is even shown.
  // A v2 checkbox token is single-use, so it's reset (forcing re-verification)
  // after every submit attempt, success or failure — but resetting the
  // captcha never touches formik's state, so a failed/blocked attempt never
  // loses anything the parent typed.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        session: Yup.string().required("Session is required"),
        system: Yup.string().required("System is required"),
        appliedClass: Yup.string().required("Class is required"),
        firstName: Yup.string().required("First name is required"),
        lastName: Yup.string(),
        gender: Yup.string().required("Gender is required"),
        dateOfBirth: Yup.date()
          .max(new Date(), "Date of birth cannot be in future")
          .required("Date of birth is required"),

        fatherName: Yup.string().required("Father's name is required"),
        fatherPhoneNumber: Yup.string().required(
          "Father's phone number is required"
        ),
        fatherEmail: Yup.string().email("Invalid email"),

        motherName: Yup.string(),

        branch: Yup.string().required("Branch is required"),
      }),
    []
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: false,
    onSubmit: async (values, { setSubmitting }) => {
      if (!captchaToken) {
        toast.error("Please complete the verification below before submitting.");
        setSubmitting(false);
        return;
      }

      try {
        const phoneFields = ["fatherPhoneNumber"];

        const form = new FormData();
        Object.entries(values).forEach(([key, val]) => {
          if (val === undefined || val === null || val === "") return;
          if (phoneFields.includes(key)) {
            const transformed = phoneTransform(String(val));
            if (transformed) form.append(key, transformed);
            return;
          }
          form.append(key, String(val));
        });
        form.append("recaptchaToken", captchaToken);

        const result = await submitRegistration(form);
        toast.success("Registration submitted successfully!");
        onSubmitted(result?.registrationNumber);
      } catch (err: any) {
        const data = err?.response?.data;
        if (data?.message) {
          toast.error(data.message);
        } else if (data?.errors && typeof data.errors === "object") {
          const first = Object.values(data.errors).flat()[0];
          toast.error(typeof first === "string" ? first : "Submission failed");
        } else {
          toast.error(
            err?.message || "Network error. Please try again."
          );
        }
      } finally {
        // A v2 token is single-use — reset it (success or failure) so a retry
        // always re-verifies. This never touches formik's values, so nothing
        // the parent typed is lost on a failed attempt.
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
        setSubmitting(false);
      }
    },
  });

  // Sessions/systems are per-campus — refetch whenever the selected campus
  // changes. No "auto-pick a default system" here (the old Eon site
  // auto-selected O-Levels, which doesn't generalize — different campuses
  // default to different systems entirely), the parent just picks both.
  useEffect(() => {
    const branch = formik.values.branch;
    if (!branch) {
      setSessions([]);
      setSystems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [ses, sys] = await Promise.all([
          getAllSessions(branch),
          getAllSystems(branch),
        ]);
        if (cancelled) return;
        setSessions(Array.isArray(ses) ? ses : []);
        setSystems(Array.isArray(sys) ? sys : []);
      } catch {
        if (!cancelled) {
          setSessions([]);
          setSystems([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.branch]);

  // load classes when system changes
  useEffect(() => {
    const sys = formik.values.system;
    if (!sys) {
      setClasses([]);
      return;
    }
    (async () => {
      try {
        const list = await getClassesBySystem(sys);
        setClasses(Array.isArray(list) ? list : []);
      } catch {
        setClasses([]);
      }
    })();
    formik.setFieldValue("appliedClass", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.system]);

  const fieldError = (name: keyof typeof initialValues) => {
    const touched = (formik.touched as any)[name];
    const error = (formik.errors as any)[name];
    return touched && error ? <div className="text-danger small">{error}</div> : null;
  };

  const inputClass = (name: keyof typeof initialValues) => {
    const touched = (formik.touched as any)[name];
    const error = (formik.errors as any)[name];
    return `form-control ${touched && error ? "is-invalid" : ""}`;
  };

  return (
    <form onSubmit={formik.handleSubmit} className="registration-form">
      <div className="card mb-3">
        <div className="card-header bg-light">
          <h5 className="mb-0">Campus</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Campus *</label>
              <select
                className={inputClass("branch")}
                name="branch"
                value={formik.values.branch}
                onChange={(e) => {
                  const nextBranch = e.target.value;
                  formik.setFieldValue("branch", nextBranch);
                  // Session/system/class only make sense for the campus they
                  // were fetched under — clear them so a stale selection from
                  // the previous campus can't slip through.
                  formik.setFieldValue("session", "");
                  formik.setFieldValue("system", "");
                  formik.setFieldValue("appliedClass", "");
                  onCampusChange(nextBranch);
                }}
                onBlur={formik.handleBlur}
              >
                <option value="">Select campus</option>
                {campuses.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {fieldError("branch")}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-light">
          <h5 className="mb-0">Student Information</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Session *</label>
              <select
                className={inputClass("session")}
                name="session"
                value={formik.values.session}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select session</option>
                {sessions.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.title}
                  </option>
                ))}
              </select>
              {fieldError("session")}
            </div>
            <div className="col-md-4">
              <label className="form-label">System *</label>
              <select
                className={inputClass("system")}
                name="system"
                value={formik.values.system}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select system</option>
                {systems.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {fieldError("system")}
            </div>
            <div className="col-md-4">
              <label className="form-label">
                Class / Grade Seeking Admission For *
              </label>
              <select
                className={inputClass("appliedClass")}
                name="appliedClass"
                value={formik.values.appliedClass}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select class</option>
                {classes.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {fieldError("appliedClass")}
            </div>

            <div className="col-md-4">
              <label className="form-label">First Name *</label>
              <input
                className={inputClass("firstName")}
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {fieldError("firstName")}
            </div>
            <div className="col-md-4">
              <label className="form-label">Last Name</label>
              <input
                className={inputClass("lastName")}
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {fieldError("lastName")}
            </div>
            <div className="col-md-4">
              <label className="form-label">Gender *</label>
              <select
                className={inputClass("gender")}
                name="gender"
                value={formik.values.gender}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select</option>
                {gender.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
              {fieldError("gender")}
            </div>

            <div className="col-md-4">
              <label className="form-label">Date of Birth *</label>
              <input
                type="date"
                className={inputClass("dateOfBirth")}
                name="dateOfBirth"
                max={new Date().toISOString().split("T")[0]}
                value={formik.values.dateOfBirth}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {fieldError("dateOfBirth")}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-light">
          <h5 className="mb-0">Father's Information</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Father Name *</label>
              <input
                className={inputClass("fatherName")}
                name="fatherName"
                value={formik.values.fatherName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {fieldError("fatherName")}
            </div>
            <div className="col-md-4">
              <label className="form-label">Phone (WhatsApp) *</label>
              <div className="input-group">
                <span className="input-group-text">+92</span>
                <input
                  className={inputClass("fatherPhoneNumber")}
                  name="fatherPhoneNumber"
                  placeholder="3001234567"
                  maxLength={10}
                  value={formik.values.fatherPhoneNumber}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                    formik.setFieldValue("fatherPhoneNumber", v);
                  }}
                  onBlur={formik.handleBlur}
                />
              </div>
              {fieldError("fatherPhoneNumber")}
            </div>
            <div className="col-md-4">
              <label className="form-label">Email</label>
              <input
                type="email"
                className={inputClass("fatherEmail")}
                name="fatherEmail"
                value={formik.values.fatherEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {fieldError("fatherEmail")}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-light">
          <h5 className="mb-0">Mother's Information</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Mother Name</label>
              <input
                className={inputClass("motherName")}
                name="motherName"
                value={formik.values.motherName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {fieldError("motherName")}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-light">
          <h5 className="mb-0">Address</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Area</label>
              <select
                className="form-control"
                name="area"
                value={formik.values.area}
                onChange={formik.handleChange}
              >
                <option value="">Select area</option>
                {karachiAreas.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-light">
          <h5 className="mb-0">How did you hear about us?</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <select
                className="form-control"
                name="referrer"
                value={formik.values.referrer}
                onChange={(e) => {
                  formik.handleChange(e);
                  setOtherRef(e.target.value === "Other");
                  if (e.target.value !== "Other") {
                    formik.setFieldValue("otherReferrer", "");
                  }
                }}
              >
                <option value="">Select</option>
                {referrers.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            {otherRef && (
              <div className="col-md-4">
                <label className="form-label">Specify</label>
                <input
                  className="form-control"
                  name="otherReferrer"
                  value={formik.values.otherReferrer}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-center">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={SITE_KEY}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button
          type="submit"
          className="btn btn-primary px-4"
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting ? "Submitting..." : "Submit Registration"}
        </button>
      </div>
    </form>
  );
};

export default RegistrationForm;
